"use client";

import { Translate } from "@/components/Translate";
import { AssetBlok, multiAssetToSingleAsset, sizes } from "@/components/bloks/Asset";
import {
	DrawerClose,
	ModalContent,
	ModalMain,
	ModalRoot,
	ModalViewport,
} from "@/components/ui/Drawer";
import { RichTextRender } from "@/components/ui/RichTextRender";
import { useLang } from "@/i18n/client";
import { StoryblokComponent } from "@frend-digital/storyblok";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { useQuery } from "@tanstack/react-query";
import React, { Suspense, useState } from "react";
import { rpc } from "#rpc";
import styles from "./AccountCreatedModal.module.css";
import { Button } from "@/components/ui/Button";
import { CopyIcon } from "@/components/icons/CopyIcon";

type AccountCreatedModalProps = {
	preview?: boolean;
};

const UnsuspendedAccountCreatedModal = ({ preview = false }: AccountCreatedModalProps) => {
	const searchParams = useUrlSearchParams();
	const language = useLang();
	const [copied, setCopied] = useState(false);

	const urlParam = searchParams.get("accountCreated") === "true";
	const sessionStorageValue =
		typeof window !== "undefined" ? sessionStorage.getItem("accountCreated") : null;
	const sessionFlag = sessionStorageValue === "true";
	const showModal = urlParam || sessionFlag;

	const { data: account } = useQuery({
		queryKey: ["account-story", language],
		queryFn: async () => {
			const result = await rpc.stories.getAccount(language);
			return result;
		},
		enabled: showModal,
		staleTime: 1000 * 5 * 60,
	});

	const rawModalData = account?.content.accountPopup;
	const modalData = Array.isArray(rawModalData) ? rawModalData[0] : rawModalData;

	const handleCopy = async () => {
		if (modalData?.voucherCode) {
			await navigator.clipboard.writeText(modalData.voucherCode);
			setCopied(true);
		}
	};

	const handleClose = () => {
		searchParams.delete("accountCreated");
		if (typeof window !== "undefined") {
			sessionStorage.removeItem("accountCreated");
		}
		setCopied(false);
	};

	if (!modalData) return null;

	return (
		<ModalRoot open={showModal} onOpenChange={handleClose}>
			<ModalViewport lock className={styles.viewport}>
				<ModalContent>
					<header className={styles.header}>
						<DrawerClose className={styles.close} />
					</header>
					<ModalMain className={styles.main}>
						<div className={styles.content}>
							<div className={styles.leftSection}>
								<h2 className={styles.title}>
									{modalData.title || <Translate>Account created!</Translate>}
								</h2>

								{modalData.description && (
									<RichTextRender
										small
										blok={modalData.description}
										preview={preview}
										className={styles.description}
									/>
								)}

								{modalData.voucherCode && (
									<div className={styles.discountContainer}>
										<Button
											type="button"
											className={styles.discountField}
											onClick={handleCopy}
											aria-label={`Copy voucher code ${modalData.voucherCode}`}
										>
											{!copied ? (
												<>
													<span className={styles.discountCode}>{modalData.voucherCode}</span>
													<span className={styles.copyButton} aria-hidden="true">
														<CopyIcon />
													</span>
												</>
											) : (
												<span className={styles.copiedText}>
													<Translate>Copied!</Translate>
												</span>
											)}
										</Button>
									</div>
								)}

								<div className={styles.actions}>
									{modalData.button?.map((button) => (
										<StoryblokComponent blok={button} preview={preview} key={button._uid} />
									))}
								</div>
							</div>

							{modalData.media && (
								<div className={styles.rightSection}>
									<div className={styles.imageWrapper}>
										<AssetBlok
											source={multiAssetToSingleAsset(modalData.media)}
											sizes={sizes(70, 60)}
										/>
									</div>
								</div>
							)}
						</div>
					</ModalMain>
				</ModalContent>
			</ModalViewport>
		</ModalRoot>
	);
};

export const AccountCreatedModal = ({ preview = false }: AccountCreatedModalProps = {}) => {
	return (
		<Suspense fallback={null}>
			<UnsuspendedAccountCreatedModal preview={preview} />
		</Suspense>
	);
};
